<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-secondary-900 dark:text-white">Email Integration</h1>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure email-to-ticket automation to create support tickets from incoming emails
          </p>
        </div>
        <button
          @click="showInfo = true"
          class="p-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <InformationCircleIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- Email Server Settings -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
            Email Server Configuration
          </h3>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure IMAP settings to receive support emails
          </p>
        </div>
        <div class="p-6">
          <form @submit.prevent="saveEmailSettings" class="space-y-6">
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <!-- Host -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  IMAP Host *
                </label>
                <input
                  v-model="form.host"
                  type="text"
                  required
                  placeholder="imap.gmail.com"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                />
              </div>

              <!-- Port -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Port *
                </label>
                <input
                  v-model.number="form.port"
                  type="number"
                  required
                  placeholder="993"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                />
              </div>

              <!-- Email -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Email Address *
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="support@company.com"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                />
              </div>

              <!-- Password -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Password *
                </label>
                <input
                  v-model="form.password"
                  type="password"
                  placeholder="••••••••"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                />
                <p class="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                  Leave blank to keep current password
                </p>
              </div>

              <!-- Encryption -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Encryption
                </label>
                <select
                  v-model="form.encryption"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                >
                  <option value="ssl">SSL</option>
                  <option value="tls">TLS</option>
                </select>
              </div>

              <!-- Enabled -->
              <div class="flex items-center pt-6">
                <input
                  v-model="form.enabled"
                  type="checkbox"
                  id="enabled"
                  class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded"
                />
                <label for="enabled" class="ml-2 block text-sm text-secondary-900 dark:text-white font-medium">
                  Enable email-to-ticket automation
                </label>
              </div>
            </div>

            <!-- Connection Test Result -->
            <div v-if="testResult" class="rounded-lg p-4" :class="[
              testResult.success
                ? 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800'
                : 'bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800'
            ]">
              <p class="text-sm" :class="[
                testResult.success
                  ? 'text-success-700 dark:text-success-300'
                  : 'text-danger-700 dark:text-danger-300'
              ]">
                {{ testResult.message }}
              </p>
            </div>

            <div class="flex space-x-3">
              <button
                type="button"
                @click="testConnection"
                :disabled="testing"
                class="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                <span v-if="!testing">Test Connection</span>
                <span v-else>Testing...</span>
              </button>
              <button
                type="submit"
                :disabled="saving"
                class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                <span v-if="!saving">Save Settings</span>
                <span v-else>Saving...</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Email Parsing Rules -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
            Parsing Rules
          </h3>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure how emails are processed into tickets
          </p>
        </div>
        <div class="p-6">
          <div class="space-y-6">
            <!-- Auto-assign rules -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Auto-assign tickets to
              </label>
              <select
                v-model="parsingRules.auto_assign_to"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option :value="null">No auto-assignment</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
                </option>
              </select>
            </div>

            <!-- Default category -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Default Category
              </label>
              <select
                v-model="parsingRules.default_category"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option :value="null">No default category</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </div>

            <!-- Default priority -->
            <div>
              <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Default Priority
              </label>
              <select
                v-model="parsingRules.default_priority"
                class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <!-- Settings checkboxes -->
            <div class="space-y-3">
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    v-model="parsingRules.strip_signatures"
                    type="checkbox"
                    id="strip_signatures"
                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded"
                  />
                </div>
                <div class="ml-3">
                  <label for="strip_signatures" class="text-sm font-medium text-secondary-900 dark:text-white">
                    Strip email signatures
                  </label>
                  <p class="text-xs text-secondary-500 dark:text-secondary-400">
                    Remove email signatures from ticket description
                  </p>
                </div>
              </div>

              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    v-model="parsingRules.remove_quoted_text"
                    type="checkbox"
                    id="remove_quoted_text"
                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded"
                  />
                </div>
                <div class="ml-3">
                  <label for="remove_quoted_text" class="text-sm font-medium text-secondary-900 dark:text-white">
                    Remove quoted text
                  </label>
                  <p class="text-xs text-secondary-500 dark:text-secondary-400">
                    Remove quoted previous emails from description
                  </p>
                </div>
              </div>

              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    v-model="parsingRules.save_attachments"
                    type="checkbox"
                    id="save_attachments"
                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded"
                  />
                </div>
                <div class="ml-3">
                  <label for="save_attachments" class="text-sm font-medium text-secondary-900 dark:text-white">
                    Save attachments
                  </label>
                  <p class="text-xs text-secondary-500 dark:text-secondary-400">
                    Save email attachments to ticket
                  </p>
                </div>
              </div>

              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    v-model="parsingRules.send_confirmation"
                    type="checkbox"
                    id="send_confirmation"
                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded"
                  />
                </div>
                <div class="ml-3">
                  <label for="send_confirmation" class="text-sm font-medium text-secondary-900 dark:text-white">
                    Send confirmation email
                  </label>
                  <p class="text-xs text-secondary-500 dark:text-secondary-400">
                    Send confirmation email with ticket number to sender
                  </p>
                </div>
              </div>
            </div>

            <div class="pt-4">
              <button
                @click="saveParsingRules"
                :disabled="savingRules"
                class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                <span v-if="!savingRules">Save Parsing Rules</span>
                <span v-else>Saving...</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Email Activity -->
      <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
        <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
          <div class="flex items-center justify-between">
            <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
              Recent Email Activity
            </h3>
            <button
              @click="refreshActivity"
              class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
            <thead class="bg-secondary-50 dark:bg-secondary-900/50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                  Date
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                  From
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                  Subject
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                  Ticket
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
              <tr v-if="recentActivity.length === 0">
                <td colspan="5" class="px-6 py-8 text-center text-sm text-secondary-500 dark:text-secondary-400">
                  No recent email activity
                </td>
              </tr>
              <tr v-for="activity in recentActivity" :key="activity.id" class="hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                  {{ formatDate(activity.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                  {{ activity.from_email }}
                </td>
                <td class="px-6 py-4 text-sm text-secondary-900 dark:text-white">
                  {{ activity.subject }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <a
                    v-if="activity.ticket_number"
                    :href="`/tickets/${activity.ticket_id}`"
                    class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  >
                    #{{ activity.ticket_number }}
                  </a>
                  <span v-else class="text-secondary-400 dark:text-secondary-500">-</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="[
                      'px-2 py-1 text-xs font-medium rounded-full',
                      activity.status === 'success' ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300' : 'bg-danger-100 dark:bg-danger-900/30 text-danger-800 dark:text-danger-300'
                    ]"
                  >
                    {{ activity.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Cron Setup Instructions -->
      <div class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
        <div class="flex">
          <InformationCircleIcon class="h-5 w-5 text-primary-600 dark:text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
          <div class="flex-1">
            <h4 class="text-sm font-medium text-primary-900 dark:text-primary-200 mb-2">
              Cron Job Setup Required
            </h4>
            <p class="text-sm text-primary-800 dark:text-primary-300 mb-2">
              To automatically process incoming emails, add this to your server's crontab:
            </p>
            <code class="block bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white px-4 py-2 rounded text-sm font-mono">
              * * * * * cd /path/to/project && php artisan emails:parse >> /dev/null 2>&1
            </code>
            <p class="text-xs text-primary-700 dark:text-primary-400 mt-2">
              This will check for new emails every minute
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <Modal :show="showInfo" @close="showInfo = false">
      <div class="p-6">
        <div class="flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mx-auto">
          <InformationCircleIcon class="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 class="mt-4 text-lg font-medium text-secondary-900 dark:text-white text-center">
          Email Integration
        </h3>
        <div class="mt-4 space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
          <p>
            <strong class="text-secondary-900 dark:text-white">What is email-to-ticket?</strong><br>
            Automatically create support tickets from incoming emails to your support email address.
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">How to set up:</strong><br>
            1. Enter your IMAP email server details<br>
            2. Test the connection to verify settings<br>
            3. Configure parsing rules and auto-assignment<br>
            4. Enable the integration<br>
            5. Set up a cron job to run: <code class="text-xs bg-secondary-100 dark:bg-secondary-700 px-1 py-0.5 rounded">php artisan emails:parse</code>
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">Common providers:</strong><br>
            • Gmail: imap.gmail.com:993 (SSL)<br>
            • Outlook: outlook.office365.com:993 (SSL)<br>
            • Yahoo: imap.mail.yahoo.com:993 (SSL)
          </p>
          <p class="text-xs">
            <strong class="text-secondary-900 dark:text-white">Note:</strong> For Gmail, you need to use an App Password instead of your regular password.
          </p>
        </div>
        <div class="mt-6">
          <button
            @click="showInfo = false"
            class="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 font-medium"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Modal from '@/Components/Common/Modal.vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  settings: {
    type: Object,
    default: () => ({}),
  },
  users: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    default: () => [],
  },
  recentActivity: {
    type: Array,
    default: () => [],
  },
});

const showInfo = ref(false);
const testing = ref(false);
const saving = ref(false);
const savingRules = ref(false);
const testResult = ref(null);

const form = ref({
  host: props.settings?.host || '',
  port: props.settings?.port || 993,
  email: props.settings?.email || '',
  password: '',
  encryption: props.settings?.encryption || 'ssl',
  enabled: props.settings?.enabled || false,
});

const parsingRules = ref({
  auto_assign_to: props.settings?.auto_assign_to || null,
  default_category: props.settings?.default_category || null,
  default_priority: props.settings?.default_priority || 'medium',
  strip_signatures: props.settings?.strip_signatures ?? true,
  remove_quoted_text: props.settings?.remove_quoted_text ?? true,
  save_attachments: props.settings?.save_attachments ?? true,
  send_confirmation: props.settings?.send_confirmation ?? true,
});

const saveEmailSettings = () => {
  saving.value = true;
  testResult.value = null;

  router.post('/settings/email-integration', form.value, {
    preserveScroll: true,
    onFinish: () => {
      saving.value = false;
    },
  });
};

const testConnection = () => {
  testing.value = true;
  testResult.value = null;

  router.post('/settings/email-integration/test', form.value, {
    preserveScroll: true,
    onSuccess: (page) => {
      testResult.value = page.props.flash?.testResult || null;
    },
    onFinish: () => {
      testing.value = false;
    },
  });
};

const saveParsingRules = () => {
  savingRules.value = true;

  router.put('/settings/email-integration/parsing-rules', parsingRules.value, {
    preserveScroll: true,
    onFinish: () => {
      savingRules.value = false;
    },
  });
};

const refreshActivity = () => {
  router.reload({ only: ['recentActivity'] });
};

const formatDate = (date) => {
  return new Date(date).toLocaleString();
};
</script>
